import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navItems, siteConfig } from "@/mocks/site";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const activePath = useMemo(() => location.pathname, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === "/") return activePath === "/";
    return activePath.startsWith(href);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="flex h-16 items-center justify-between px-4 md:h-20 md:px-10">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-book-2-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
              {siteConfig.name}
            </span>
            <span className="mt-0.5 hidden text-[11px] font-label tracking-[0.18em] text-foreground-500 sm:block">
              EVENT WIKI
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`relative whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "text-primary-600"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
              {isActive(item.href) && (
                <span className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-primary-500" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/entries"
            className="hidden whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            开始查词条
          </Link>
          <button
            type="button"
            aria-label={open ? "关闭菜单" : "打开菜单"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {open && (
        <nav
          className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 md:hidden"
          aria-label="移动端导航"
        >
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`block rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "bg-primary-50 text-primary-600"
                  : "text-foreground-800 hover:bg-background-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/entries"
            className="mt-2 block whitespace-nowrap rounded-md bg-foreground-950 px-3 py-3 text-center text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            开始查词条
          </Link>
        </nav>
      )}
    </header>
  );
}